/**
 * MUTAGIM.SHOP — Admin Audit Logs Page (Phase 10)
 * Visualisation des logs d'audit avec export CSV
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, Download, Search, Calendar, Filter,
  Loader2, RefreshCw, ArrowLeft, User
} from 'lucide-react';
import { AdminLayout } from '../components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminAuditLogsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [availableActions, setAvailableActions] = useState([]);
  const [filters, setFilters] = useState({
    action: '',
    admin_email: '',
    date_from: '',
    date_to: '',
  });
  const [page, setPage] = useState(0);
  const limit = 50;
  
  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchLogs();
  }, [filters, page]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const url = new URL(`${API_URL}/api/admin/security/audit-logs`);
      url.searchParams.set('limit', limit.toString());
      url.searchParams.set('skip', (page * limit).toString());
      
      if (filters.action) url.searchParams.set('action', filters.action);
      if (filters.admin_email) url.searchParams.set('admin_email', filters.admin_email);
      if (filters.date_from) url.searchParams.set('date_from', filters.date_from);
      if (filters.date_to) url.searchParams.set('date_to', filters.date_to);

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
        setTotal(data.total || 0);
        setAvailableActions(data.available_actions || []);
      }
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = async () => {
    try {
      const url = new URL(`${API_URL}/api/admin/security/audit-logs/export`);
      if (filters.action) url.searchParams.set('action', filters.action);
      if (filters.admin_email) url.searchParams.set('admin_email', filters.admin_email);
      if (filters.date_from) url.searchParams.set('date_from', filters.date_from);
      if (filters.date_to) url.searchParams.set('date_to', filters.date_to);

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `audit_logs_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success('Export CSV téléchargé');
      }
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Erreur d\'export');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatDetails = (details) => {
    if (!details || typeof details !== 'object') return '-';
    try {
      return JSON.stringify(details, null, 2).slice(0, 200) + (JSON.stringify(details).length > 200 ? '...' : '');
    } catch {
      return '-';
    }
  };

  const getActionColor = (action) => {
    if (action?.includes('delete') || action?.includes('remove') || action?.includes('reject')) return 'text-red-400';
    if (action?.includes('add') || action?.includes('create') || action?.includes('approve')) return 'text-green-400';
    if (action?.includes('update') || action?.includes('edit')) return 'text-blue-400';
    return 'text-gray-200';
  };

  return (
    <div className="min-h-screen bg-mutagim-dark">
      {/* Header */}
      <header className="bg-gray-900 border-b border-gray-800 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin')}
              className="text-gray-200"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <FileText className="w-6 h-6 text-mutagim-red" />
            <h1 className="text-xl font-bold text-white">Audit Logs</h1>
            <span className="text-gray-500 text-sm ml-2">({total} entrées)</span>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={exportCsv}
              className="border-gray-700 text-white"
            >
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </Button>
            <Button
              variant="outline"
              onClick={fetchLogs}
              className="border-gray-700 text-white"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        {/* Filtres */}
        <div className="mb-6 p-4 bg-gray-900 rounded-lg border border-gray-800">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm text-gray-200 mb-1">Action</label>
              <select
                value={filters.action}
                onChange={(e) => { setFilters(f => ({ ...f, action: e.target.value })); setPage(0); }}
                className="w-full bg-gray-800 border-gray-700 text-white rounded-md px-3 py-2"
              >
                <option value="">Toutes les actions</option>
                {availableActions.map(action => (
                  <option key={action} value={action}>{action}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-200 mb-1">Admin</label>
              <Input
                placeholder="Email admin..."
                value={filters.admin_email}
                onChange={(e) => { setFilters(f => ({ ...f, admin_email: e.target.value })); setPage(0); }}
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-200 mb-1">Date début</label>
              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) => { setFilters(f => ({ ...f, date_from: e.target.value })); setPage(0); }}
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-200 mb-1">Date fin</label>
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) => { setFilters(f => ({ ...f, date_to: e.target.value })); setPage(0); }}
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>
          </div>
        </div>

        {/* Info rétention */}
        <div className="mb-6 p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg text-sm text-gray-200">
          Rétention: 90 jours. Les logs plus anciens sont automatiquement supprimés.
        </div>

        {/* Liste */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-mutagim-red" />
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-16">
            <FileText className="w-16 h-16 mx-auto mb-4 text-gray-700" />
            <p className="text-gray-200">Aucun log trouvé</p>
          </div>
        ) : (
          <>
            <div className="bg-gray-900 rounded-lg border border-gray-800 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-200">Date</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-200">Action</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-200">Admin</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-200">Détails</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-200">IP</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {logs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-800/50">
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-200">{formatDate(log.created_at)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={cn("text-sm font-mono", getActionColor(log.action))}>
                          {log.action}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-500" />
                          <span className="text-sm text-white">{log.admin_email || '-'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <pre className="text-xs text-gray-200 max-w-xs truncate">
                          {formatDetails(log.details)}
                        </pre>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-gray-500 font-mono">{log.ip_address || '-'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {total > limit && (
              <div className="mt-4 flex items-center justify-between">
                <span className="text-sm text-gray-200">
                  Page {page + 1} sur {Math.ceil(total / limit)}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 0}
                    onClick={() => setPage(p => p - 1)}
                    className="border-gray-700 text-white"
                  >
                    Précédent
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={(page + 1) * limit >= total}
                    onClick={() => setPage(p => p + 1)}
                    className="border-gray-700 text-white"
                  >
                    Suivant
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

export default AdminAuditLogsPage;
